import { useEffect, useRef, useState } from "react";
import { Cpu, Loader2, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { useApp } from "../App";

/**
 * Välj vilka AI-motorer som ska analysera bilderna. Inget analyseras automatiskt om inte
 * admin slagit på det under Inställningar – det här är det vanliga sättet att starta en analys.
 */
export function AnalyzePanel({ ids, onDone, compact }: { ids: string[]; onDone: (msg: string) => void; compact?: boolean }) {
  const { status, bump } = useApp();
  const ai = status?.ai;
  const agentMode = ai?.visionMode === "agent";
  const agentOnline = !!status?.agent?.online;
  const [vision, setVision] = useState(!!ai?.vision);
  const [claude, setClaude] = useState(false);
  const [busy, setBusy] = useState(false);
  const n = ids.length;

  return (
    <div className={compact ? "" : "w-80"}>
      <label className={`flex gap-2.5 rounded-lg p-2 ${ai?.vision ? "cursor-pointer hover:bg-mist" : "opacity-50"}`}>
        <input type="checkbox" className="mt-1 accent-ember" checked={vision} disabled={!ai?.vision} onChange={(e) => setVision(e.target.checked)} />
        <div>
          <div className="flex items-center gap-1.5 font-medium"><Cpu size={14} /> Apple Vision</div>
          <div className="text-[12px] leading-snug text-soft">
            Motiv, ansikten, text i bild och fokuspunkt.{" "}
            {!ai?.vision ? "Inte tillgänglig." : agentMode
              ? agentOnline ? "Körs på Mac-agenten – bilden lämnar inte banken." : "Mac-agenten är offline – körs när den ansluter."
              : "Körs på servern."}
          </div>
        </div>
      </label>
      <label className={`flex gap-2.5 rounded-lg p-2 ${ai?.claude ? "cursor-pointer hover:bg-mist" : "opacity-50"}`}>
        <input type="checkbox" className="mt-1 accent-ember" checked={claude} disabled={!ai?.claude} onChange={(e) => setClaude(e.target.checked)} />
        <div>
          <div className="flex items-center gap-1.5 font-medium"><Sparkles size={14} /> Claude</div>
          <div className="text-[12px] leading-snug text-soft">
            Svenska taggar, förslag på alt-text och titel.{" "}
            {ai?.claude ? "Bilden skickas till Anthropic för analys." : "Kräver ANTHROPIC_API_KEY på servern."}
          </div>
        </div>
      </label>
      <button className="btn-primary mt-2 w-full justify-center" disabled={busy || (!vision && !claude)} onClick={async () => {
        setBusy(true);
        try {
          const r = await api<{ queued: { vision: number; claude: number } }>("analyze", { method: "POST", json: { ids, vision, claude } });
          const parts = [r.queued.vision && `${r.queued.vision} till Apple Vision`, r.queued.claude && `${r.queued.claude} till Claude`].filter(Boolean);
          bump();
          onDone(parts.length ? `Köade ${parts.join(" och ")}.` : "Inget att köa.");
        } finally { setBusy(false); }
      }}>
        {busy && <Loader2 size={14} className="animate-spin" />} Analysera {n} bild{n === 1 ? "" : "er"}
      </button>
    </div>
  );
}

/** Knapp med popover – för markeringslisten i biblioteket. */
export function AnalyzeButton({ ids, onMessage }: { ids: string[]; onMessage: (m: string) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  return (
    <div ref={ref} className="relative">
      <button className="flex items-center gap-1.5 rounded-lg bg-white/10 px-2.5 py-1 hover:bg-white/20" onClick={() => setOpen(!open)}>
        <Sparkles size={14} /> Analysera
      </button>
      {open && (
        <div className="absolute bottom-full left-1/2 mb-2 -translate-x-1/2 rounded-xl border border-line bg-card p-2 text-ink shadow-2xl">
          <AnalyzePanel ids={ids} onDone={(m) => { setOpen(false); onMessage(m); }} />
        </div>
      )}
    </div>
  );
}
